import { buildPersistentApi } from '../../../../../webmodule-common/cache/persistent-api';
import { getCurrentUser } from '../../../../../webmodule-common/other/api/current-user';
import { emptyGuid } from '../../../../../webmodule-common/other/api/empty-guid';
import { isDevSite } from '../../../../../webmodule-common/other/domain';
import { stagedExecution } from '../../../../../webmodule-common/other/general/event-source-client';
import { defaultStagedThreshold } from '../../../../../webmodule-common/other/general/staged-event';
import { notificationSignal } from '../../../../../webmodule-common/other/ui/icons/icon-notification-signal';

import { getApiFactory } from '../../../../api/api-injector';
import { ResultGetBranchQuoteDefaults } from '../../../../api/dealer-api-interface-franchisee';
import { WMEventSource } from '../../../../api/event-source';
import { SSEBranchQuoteDefaults } from '../../../../quotes/data/v6/sse-branch-quote-defaults.';
import { v6ConfigSupplierOptions } from '../../../../v6config/v6config';
import { getSupplier } from '../../supplier-selector';

export async function getBranchQuoteDefaultsForCurrentUser(
  supplierId: string,
  branchId: string
): Promise<unknown | null> {
  const userId = getCurrentUser()?.id ?? emptyGuid;
  const defaults = await branchQuoteDefaultsApi.run({ branchId, supplierId, systemUserId: userId });
  if (!defaults) return null;
  const primary =
    defaults?.branchQuoteDefaultsPrimary.find(x => x.systemUserId === userId) ??
    defaults?.branchQuoteDefaultsPrimary.find(x => x.systemUserId === emptyGuid);

  const defaultOptions = primary
    ? (defaults?.branchQuoteDefaults.find(x => x.id === primary.branchQuoteDefaultsId) ?? null)
    : null;
  return defaultOptions?.data ? JSON.parse(defaultOptions?.data) : null;
}

export const branchQuoteDefaultsApi = buildPersistentApi<
  {
    branchId: string;
    supplierId: string;
    systemUserId: string;
  },
  ResultGetBranchQuoteDefaults
>({
  cacheKey: 'v6BranchQuoteDefaultOptions',
  expiryTime: 86400, // 24 hours in seconds
  allowNullResults: false,
  getVersion: async () => {
    const isDev = isDevSite();
    const settings = v6ConfigSupplierOptions((await getSupplier()).supplierId);
    const instanceId = isDev ? settings.instanceId : settings.version;
    const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return `${instanceId}-${date}`;
  },
  flushEventTrigger: async _flushEvent => {
    //do nothing
  },
  apiFunction: async input => {
    return await getApiFactory()
      .franchisee()
      .getBranchQuoteDefaults(input.branchId, input.supplierId, input.systemUserId);
  }
});

export async function getBranchQuoteDefaultsForSupplier(
  supplierId: string,
  branchId: string,
  userId: string
): Promise<unknown | null> {
  const defaults = await branchQuoteDefaultsApi.run({ branchId, supplierId, systemUserId: userId });
  const primary =
    defaults?.branchQuoteDefaultsPrimary.find(x => x.systemUserId === userId) ??
    defaults?.branchQuoteDefaultsPrimary.find(x => x.systemUserId === emptyGuid);

  const defaultOptions = primary
    ? (defaults?.branchQuoteDefaults.find(x => x.id === primary.branchQuoteDefaultsId) ?? null)
    : null;
  let options = defaultOptions?.data ? JSON.parse(defaultOptions?.data) : null;
  if (Array.isArray(options) && options.length === 0) {
    options = null;
  }
  return options;
}

export function registerBranchQuoteDefaultsSSEListener(): void {
  const event = stagedExecution({
    threshold: defaultStagedThreshold,

    eventFinally: () => notificationSignal(false),
    event: async () => {
      //no-op
    },
    eventTriggered: (_data: `SSEBranchQuoteDefaults`) => {
      notificationSignal(true);
    },
    eventActioned: (data: SSEBranchQuoteDefaults) => {
      //doesn't matter what data is edited, we need to flush the cache for the entire user
      branchQuoteDefaultsApi.flushCache({
        branchId: data.branchId,
        supplierId: data.supplierId,
        systemUserId: getCurrentUser()?.id ?? emptyGuid
      });
    }
  });

  WMEventSource.getInstance().addEventListener(WMEventSource.branchQuoteDefaults, event);
}
